/**
 * Wake-on-MODULE-ENTRY lists (L-0858, Nick 2026-10-14: "every screen that's on the main nav needs to be
 * fully warmed up"). One list per module, kept warm while the user is in the module (hooks/useKeepWarm.ts, keepWarmSet.ts),
 * so the first button on any of its main-nav screens does not wait for a sleeping server.
 *
 * How a list is built: the de-duplicated union of the module's main-nav screens' reads and button-press calls,
 * ranked by user cold waits over 2026-10-12..14 (drops/BH17-L0858-svc-stats-3d.json), at most 12 names (the
 * wakeCallables cap), HELD-BACK comparison names left out (UNWOKEN_COMPARISON_CALLABLES until the L-0140 read),
 * and names already woken at app load with 0 cold waits left out (the 10-minute throttle would no-op them).
 * BURST: on app open the app-shell list (8 names) goes first; the keep-warm pass follows 3 s later in chunks of <= 12 names,
 * 2 s apart, each chunk staggered 150 ms (a ticket screen can add up to 12 more of its own). Names already knocked inside the
 * throttle window are skipped.
 * Method limit: the screen trace behind these lists is a LOWER BOUND (mount path, empty data, contexts mocked).
 *
 * fieldService (P11), 3-day cold / real preflights: listPendingGeneratorContacts 26/433, dataHub_listConnectionSummaries
 * 29/252, fieldService_qboRead 20/43 (never knocked before; ticket billing reads), ticketNextStep 13/476,
 * ticketDefaultOwner 11/44, createGenerator 5/6, updateGenerator 3/19, createServiceTicket 3/16,
 * recurringReviewQueue 3/4, updateServiceLog 2/116, galleryPromote 2/14, getOwnerGrid 1/1.
 */
export const WAKE_MODULES = {
  fieldService: {
    enabled: true,
    callables: [
      'dataHub_listConnectionSummaries',
      'fieldService_listPendingGeneratorContacts',
      'fieldService_qboRead',
      'fieldService_ticketNextStep',
      'fieldService_ticketDefaultOwner',
      'createGenerator',
      'updateGenerator',
      'fieldService_createServiceTicket',
      'fieldService_recurringReviewQueue',
      'updateServiceLog',
      'fieldService_galleryPromote',
      'fieldService_getOwnerGrid',
    ],
  },
  // Projects (P10), 3-day cold / real preflights: projects_qboRead 37/96 (listed before only on Invoice Matching, called from
  // equipment-bill and billing-status screens), listInstallInvoices 21/111, listAssignableEmployees 18/35, updateProject 1/9, createEquipment 1/2.
  projects: {
    enabled: true,
    callables: [
      'projects_qboRead',
      'projects_listInstallInvoices',
      'projects_listAssignableEmployees',
      'projects_updateProject',
      'projects_createEquipment',
    ],
  },
  // Billing (P4): projects_qboRead 37/96, dataHub_listConnectionSummaries 29/252, collections_createCollectionEvent 13/25,
  // getQboContactsForCustomer 11/37, collections_updateHasUnsentInvoices 9/16, listDriveFilesForServiceLogs 6/15,
  // finance_qboRead 5/195, customers_qboRead 5/5, collections_updateCustomerProfile 1/30. Names with 0 cold waits (recordTouch,
  // sendCollectionEmail, markInvoiceSent) are left out: their per-screen lists already cover them.
  billing: {
    enabled: true,
    callables: [
      'projects_qboRead',
      'dataHub_listConnectionSummaries',
      'collections_createCollectionEvent',
      'getQboContactsForCustomer',
      'collections_updateHasUnsentInvoices',
      'listDriveFilesForServiceLogs',
      'finance_qboRead',
      'customers_qboRead',
      'collections_updateCustomerProfile',
    ],
  },
  // Support (P5): dataHub_listConnectionSummaries 29/252, support_listLogins 11/13. support_dispatch / createCallLog are 0 cold already.
  support: {
    enabled: true,
    callables: ['dataHub_listConnectionSummaries', 'support_listLogins'],
  },
  // Fleet / Assets (P7): the one mount read the trace saw on its screens, dataHub_listConnectionSummaries 29/252. Small list on purpose.
  fleet: {
    enabled: true,
    callables: ['dataHub_listConnectionSummaries'],
  },
  // Quoting (P2): customers_qboRead 5/5 (Connected Data customers screen). Small list on purpose; quoting_getPriceBook had 0 calls in 3 days.
  quoting: {
    enabled: true,
    callables: ['customers_qboRead'],
  },
  // CRM (P1): generateDailyActions 22/36 cold (a mount read of the Leads screen; knocked 25 times, but only on that screen or at
  // sign-in via landing.ts, i.e. too late or too early), listLeadDriveFiles 4/27, the pin / unpin callables 2/2 and 1/1 (now crm_dispatch: those two per-action aliases were retired).
  // OVERLAP, on purpose: all four are also on config/wake/crm.ts leadList and generateDailyActions is on the landing list, so within
  // the throttle window this is a no-op; the keep-warm loop knocks each name once per 9-11 minutes, whichever list carries it.
  crm: {
    enabled: true,
    callables: ['generateDailyActions', 'listLeadDriveFiles', 'crm_dispatch'],
  },
  // Recruiting (P14): drive_uploadFile 9/27, 0 knocks in the 3-day logs; called through hooks/drive/useDrive.ts from the
  // /recruiting, /recruiting/candidates and /recruiting/jobs screens (census).
  recruiting: {
    enabled: true,
    callables: ['drive_uploadFile'],
  },
} as const;
